/**
 * Cableado del Motor_Insights al runtime selector y derivación de sus
 * insumos desde los JSONs reales de Antofagasta (tarea 3.7).
 *
 * Este módulo es el **puente** entre las vistas del SPA (Insights,
 * Observability, ExecutiveCenter) y el Motor_Insights client-side
 * (`./motorInsights`). Responsabilidades:
 *
 *   1. **Registrar** la implementación DEMO del servicio `motorInsights`
 *      en el runtime selector (`../api/runtime`) de forma idempotente, de
 *      modo que las vistas lo consuman SIEMPRE vía `runtime.motorInsights()`
 *      (R17.1, R17.2, R17.5) y nunca instancien el motor directamente.
 *
 *   2. **Derivar** las {@link SerieCrossDomain} de entrada desde los datos
 *      reales (`dmas.json`, `alarmas.json`, `gateways.json`) cargados por
 *      `../api/realData`, agregando por DMA señales de ≥2 dominios del
 *      conjunto {consumo, presión, MNF, NRW, RF, anomalías IoT, órdenes}
 *      con su evidencia de soporte (R5.1, R5.2). No fabrica datos: cada
 *      señal se respalda en una entidad real (la DMA o una Anomalia
 *      derivada de una alarma) (R5.8, R17).
 *
 *   3. **Exponer** un loader (`loadInsightsBundle`) que las vistas usan en
 *      sus `queryFn` para obtener los Insights con su Narrativa_Operacional,
 *      evidencia, confianza, Cadena_Causal y enlaces a entidades (R5.3,
 *      R5.7), todo producido por el motor a través del contrato.
 *
 * Determinismo: la derivación es pura respecto a los datos cargados; el
 * motor es determinista por clave natural (R5.6).
 */

import type { EntidadRef, Insight } from "../api/contracts";
import { loadRealData, type RealAlarma, type RealDma } from "../api/realData";
import { provideDemo, runtime } from "../api/runtime";
import { buildGraphFromData, type ProyeccionGrafo } from "./graph";
import {
  crearMotorInsightsLocal,
  type DominioInsight,
  type SenalDominio,
  type SerieCrossDomain,
} from "./motorInsights";

/** Versión de código para el determinismo del motor (R5.6). */
export const INSIGHTS_CODE_VERSION = "ai-operational-layer@3.7";

// ---------------------------------------------------------------------------
// Registro del proveedor DEMO en el runtime selector (R17.2)
// ---------------------------------------------------------------------------

let proyeccionGrafo: ProyeccionGrafo | undefined;
let registrado = false;

/**
 * Registra (una sola vez) la implementación DEMO del Motor_Insights en el
 * runtime selector. El proveedor usa el Grafo_Entidades proyectado desde
 * los datos reales (cuando ya está disponible) para enlazar cada Insight
 * con las entidades del Grafo (R5.7). Idempotente: invocarlo varias veces
 * no recrea el registro salvo que cambie la proyección del Grafo.
 */
export function registerInsightsRuntime(grafo?: ProyeccionGrafo): void {
  if (grafo) proyeccionGrafo = grafo;
  if (registrado && !grafo) return;
  provideDemo("motorInsights", () =>
    crearMotorInsightsLocal({
      grafo: proyeccionGrafo,
      // Descarte de correlaciones infundadas: hook no-op en demo (la
      // Observabilidad real se cablea en la tarea 10.x). El motor sigue
      // sin fabricar Insights (R5.8).
      onDiscard: () => {},
    }),
  );
  registrado = true;
}

// Registro temprano (sin Grafo aún) para que las llamadas síncronas del
// contrato — p.ej. `kpiIntelligence` desde ExecutiveCenter — resuelvan al
// motor demo y no al stub "no implementado".
registerInsightsRuntime();

// ---------------------------------------------------------------------------
// Derivación de las series cross-domain desde los JSONs reales (R5.1, R5.2)
// ---------------------------------------------------------------------------

/** Flags de alarma de `alarmas.json` que se proyectan como Anomalias. */
const ALARMA_FLAGS: Array<{ campo: keyof RealAlarma; etiqueta: string }> = [
  { campo: "alarm_leakage", etiqueta: "fuga" },
  { campo: "alarm_burst", etiqueta: "reventon" },
  { campo: "alarm_backflow", etiqueta: "backflow" },
  { campo: "alarm_dry", etiqueta: "tuberia_seca" },
  { campo: "alarm_manipulation", etiqueta: "manipulacion" },
  { campo: "alarm_battery", etiqueta: "bateria" },
  { campo: "alarm_permanent", etiqueta: "permanente" },
];

/** Agregados por DMA derivados de `alarmas.json`. */
interface AgregadoDma {
  /** Nº total de alarmas activas (cualquier tipo) en la DMA. */
  totalAlarmas: number;
  /** Referencia a una Anomalia representativa de la DMA (evidencia, R5.2). */
  anomaliaRef?: EntidadRef;
}

/**
 * Agrupa las alarmas por índice de DMA y elige una Anomalia representativa
 * (preferentemente de fuga) como evidencia de soporte de las señales de la
 * DMA. La referencia coincide con el `id` del nodo Anomalia que proyecta el
 * Grafo_Entidades (`${device}:${etiqueta}`), de modo que el enlace del
 * Insight con el Grafo sea consistente (R5.7).
 */
function agregarAlarmasPorDma(alarmas: RealAlarma[], nDmas: number): AgregadoDma[] {
  const agregados: AgregadoDma[] = Array.from({ length: nDmas }, () => ({ totalAlarmas: 0 }));
  for (const al of alarmas) {
    if (!Number.isInteger(al.dma_idx) || al.dma_idx < 0 || al.dma_idx >= nDmas) continue;
    const agg = agregados[al.dma_idx];
    let etiquetaActiva: string | undefined;
    for (const { campo, etiqueta } of ALARMA_FLAGS) {
      if (al[campo] === true) {
        agg.totalAlarmas += 1;
        if (etiquetaActiva === undefined || etiqueta === "fuga") etiquetaActiva = etiqueta;
      }
    }
    // Primera Anomalia representativa de la DMA (prioriza fuga).
    if (etiquetaActiva && (!agg.anomaliaRef || etiquetaActiva === "fuga")) {
      const yaEsFuga = agg.anomaliaRef?.id.endsWith(":fuga");
      if (!yaEsFuga) {
        agg.anomaliaRef = { tipo: "Anomalia", id: `${al.device}:${etiquetaActiva}` };
      }
    }
  }
  return agregados;
}

/** Construye una señal de dominio con evidencia de soporte (R5.2). */
function senal(
  dominio: DominioInsight,
  entidad_ref: EntidadRef,
  valor: number,
  ts: string,
  evidencia_ref?: EntidadRef,
): SenalDominio {
  return { dominio, entidad_ref, valor, ts, evidencia_ref };
}

/**
 * Deriva las {@link SerieCrossDomain} (una por DMA) desde los datos reales.
 * Cada serie agrega señales de varios dominios sobre la DMA como entidad
 * raíz, con evidencia real (la propia DMA y/o una Anomalia derivada de una
 * alarma). Determinista: el orden y los valores solo dependen de los datos
 * (R5.1, R5.6).
 */
export function buildSeriesFromData(dmas: RealDma[], alarmas: RealAlarma[]): SerieCrossDomain[] {
  const agregados = agregarAlarmasPorDma(alarmas, dmas.length);
  // Instante de referencia determinista (no afecta la igualdad por clave
  // natural; el motor solo usa el orden temporal interno).
  const ts = "2025-01-31T00:00:00.000Z";

  return dmas.map((dma, i) => {
    const dmaRef: EntidadRef = { tipo: "DMA", id: dma.codigo };
    const agg = agregados[i] ?? { totalAlarmas: 0 };
    const evidenciaAnom = agg.anomaliaRef;
    const senales: SenalDominio[] = [];

    // NRW: intensidad de Agua No Facturada (evidencia: la propia DMA).
    senales.push(senal("nrw", dmaRef, dma.nrw_pct, ts, dmaRef));
    // Consumo: volumen agregado normalizado a una escala manejable.
    senales.push(senal("consumo", dmaRef, dma.volumen_total / 1e8, ts, dmaRef));
    // MNF: aproximado por el nº de fugas reportadas en la DMA; evidencia en
    // una Anomalia real cuando existe.
    if (dma.fugas > 0) {
      senales.push(senal("mnf", dmaRef, dma.fugas, ts, evidenciaAnom ?? dmaRef));
    }
    // Presión: aproximada por eventos de backflow (reflujo) en la DMA.
    if (dma.backflow > 0) {
      senales.push(senal("presion", dmaRef, dma.backflow, ts, dmaRef));
    }
    // Anomalías IoT: nº total de alarmas activas en la DMA.
    if (agg.totalAlarmas > 0) {
      senales.push(senal("anomalias_iot", dmaRef, agg.totalAlarmas, ts, evidenciaAnom ?? dmaRef));
    }

    return {
      clave_natural: dma.codigo,
      entidad_raiz: dmaRef,
      senales,
    } satisfies SerieCrossDomain;
  });
}

// ---------------------------------------------------------------------------
// Loader consumido por las vistas (R5.3, R5.7) — vía runtime selector
// ---------------------------------------------------------------------------

/** Resultado del loader de Insights operacionales. */
export interface InsightsBundle {
  /** Insights fundamentados producidos por el Motor_Insights (R5.2). */
  insights: Insight[];
  /** True si hay datos reales de Antofagasta detrás de los Insights. */
  conDatosReales: boolean;
}

/**
 * Carga los datos reales, proyecta el Grafo_Entidades, deriva las series
 * cross-domain y obtiene los Insights del Motor_Insights **a través del
 * runtime selector** (`runtime.motorInsights().correlate`). Es la función
 * que las vistas usan en sus `queryFn` para renderizar Insights con su
 * Narrativa_Operacional, evidencia, confianza, Cadena_Causal y enlaces a
 * entidades (R5.3, R5.7).
 */
export async function loadInsightsBundle(): Promise<InsightsBundle> {
  const data = await loadRealData();
  if (!data || data.dmas.length === 0) {
    // Sin datos reales: el motor no fabrica Insights (R5.8).
    registerInsightsRuntime();
    return { insights: [], conDatosReales: false };
  }

  // Proyecta el Grafo_Entidades y re-registra el motor con él para enlazar
  // cada Insight con las entidades proyectadas (R5.7).
  const grafo = buildGraphFromData({ dmas: data.dmas, gateways: data.gateways, alarmas: data.alarmas });
  registerInsightsRuntime(grafo);

  const series = buildSeriesFromData(data.dmas, data.alarmas);
  const motor = runtime.motorInsights();
  const res = await motor.correlate(series, {}, INSIGHTS_CODE_VERSION);
  if (!res.ok) {
    return { insights: [], conDatosReales: true };
  }
  return { insights: res.value, conDatosReales: true };
}

/**
 * Calcula KPI intelligence (dirección + magnitud porcentual con signo +
 * causa probable) de la variación de un KPI entre dos Periodos, vía el
 * runtime selector (`runtime.motorInsights().kpiIntelligence`) (R5.5).
 * Devuelve `null` si el motor no puede calcularlo.
 */
export function kpiIntelligence(kpi: string, periodoAnterior: number, periodoActual: number) {
  const res = runtime.motorInsights().kpiIntelligence(kpi, periodoAnterior, periodoActual);
  return res.ok ? res.value : null;
}
